import React, { useMemo, useRef, useState } from 'react';
import SequenceViewer from './SequenceViewer';
import AdvancedMolStarViewer, { AdvancedMolStarViewerRef } from './AdvancedMolStarViewer';

interface TopbarStructureViewerProps {
  data: string;
  format?: 'pdb' | 'cif' | 'sdf';
  fileName?: string;
  height?: number | string;
  showSequence?: boolean;
}

const TopbarStructureViewer: React.FC<TopbarStructureViewerProps> = ({
  data,
  format = 'pdb',
  fileName,
  height = 600,
  showSequence = true
}) => {
  const molstarRef = useRef<AdvancedMolStarViewerRef>(null);

  const [representation, setRepresentation] = useState<string>('cartoon');
  const [colorScheme, setColorScheme] = useState<string>('chain-id');
  const [selectedChain, setSelectedChain] = useState<string>('all');

  const [activeHoverSource, setActiveHoverSource] = useState<'sequence' | 'structure' | null>(null);
  const [structureHover, setStructureHover] = useState<{ chainId: string; position: number; name?: string } | null>(null);
  const [seqHoverLabel, setSeqHoverLabel] = useState<string>('');

  const containerPaddingTop = useMemo(() => (showSequence ? 150 : 0), [showSequence]);

  return (
    <div style={{ height, position: 'relative' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, right: 0, zIndex: 4 }}>
        <SequenceViewer
          pdbData={data}
          fileName={fileName}
          compact
          selectedChainId={selectedChain}
          externalHovered={activeHoverSource === 'structure' ? structureHover : null}
          representation={representation}
          colorScheme={colorScheme}
          onRepresentationChange={(v) => setRepresentation(v)}
          onColorSchemeChange={(v) => setColorScheme(v)}
          onSelectedChainChange={setSelectedChain}
          onResetView={() => molstarRef.current?.resetView()}
          onScreenshot={async () => { try { await molstarRef.current?.screenshot(); } catch {} }}
          height={150}
          collapsed={!showSequence}
          sequenceActive={showSequence}
          showFileSelect={false}
          onResidueHover={(res) => {
            if (!molstarRef.current) return;
            if (res) {
              const residueName = res.name || res.code || '???';
              const label = `${residueName} ${res.position} | Chain ${res.chainId}`;
              setSeqHoverLabel(label);
              setActiveHoverSource('sequence');
              molstarRef.current.highlightResidues([res.position], res.chainId, { showLabel: true, labelText: label });
            } else {
              setSeqHoverLabel('');
              if (activeHoverSource !== 'structure') {
                molstarRef.current.clearHighlight();
                setActiveHoverSource(null);
              }
            }
          }}
        />
      </div>

      <div style={{ height: '100%', paddingTop: `${containerPaddingTop}px`, transition: 'padding-top 0.3s ease' }}>
        <AdvancedMolStarViewer
          ref={molstarRef}
          structureData={data}
          structureFormat={format}
          height="100%"
          representation={representation}
          colorScheme={colorScheme}
          showControls={false}
          onStructureHover={(info) => {
            setStructureHover(info);
            if (info) {
              // 仅当从序列悬停切换到结构悬停时，清理一次序列产生的高亮
              setActiveHoverSource(prev => {
                if (prev !== 'structure') {
                  try { molstarRef.current?.clearHighlight(); } catch {}
                }
                return 'structure';
              });
              setSeqHoverLabel('');
            } else {
              setActiveHoverSource(prev => (prev === 'structure' ? null : prev));
            }
          }}
          externalHoverLabel={activeHoverSource === 'sequence' ? seqHoverLabel : (activeHoverSource === 'structure' && structureHover ? `${structureHover.name || ''} ${structureHover.position} | Chain ${structureHover.chainId}` : undefined)}
        />
      </div>

      {(() => {
        const floatingLabel = activeHoverSource === 'sequence'
          ? (seqHoverLabel || '')
          : (activeHoverSource === 'structure' && structureHover
            ? `${structureHover.name || ''} ${structureHover.position} | Chain ${structureHover.chainId}`
            : '');
        return floatingLabel ? (
          <div style={{
            position: 'absolute',
            right: 16,
            bottom: 16,
            background: 'linear-gradient(135deg, rgba(255, 77, 79, 0.95) 0%, rgba(220, 20, 60, 0.95) 100%)',
            color: '#fff',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: 'monospace',
            fontWeight: 600,
            zIndex: 2000,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
            backdropFilter: 'blur(4px)',
            letterSpacing: '0.5px',
            lineHeight: '1.5',
            minWidth: '180px',
            textAlign: 'center'
          }}>
            <div>{floatingLabel}</div>
          </div>
        ) : null;
      })()}
    </div>
  );
};

export default TopbarStructureViewer;



